import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { setDemoReadOnly, supabase } from '../../supabaseClient';
import { buildCan } from '../accessControl';
import { isSeniorOrAbove, isSuperAdmin, SUPER_ADMIN_ID } from '../permissions';

/* ══════════════════════════════════════════════════════════════
   Modo Demo – fallback para usuários sem vínculos no banco
   Cada objeto representa uma loja que o usuário pode acessar.
   A estrutura já está preparada para suportar `arquivos` e
   `documentos` vinculados por loja no futuro.
   ══════════════════════════════════════════════════════════════ */
const DEMO_LOJAS = [
    { id: 'loja-demo', nome: 'Loja Demo', is_demo: true },
];

// Lojas demo do banco (dados fictícios, só leitura pelo RLS); sem nenhuma, cai no placeholder vazio
async function fetchDemoLojas() {
    const { data } = await supabase.from('locations').select('id, name, is_demo, store_images_url').eq('is_demo', true).order('name')
        .then((res) => res, () => ({}));
    return data?.length ? data.map((loc) => ({ id: loc.id, nome: loc.name, is_demo: true, imagem: loc.store_images_url || null })) : DEMO_LOJAS;
}

/* ══════════════════════════════════════════════════════════════
   Contexto de Autenticação
   ══════════════════════════════════════════════════════════════ */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // ── Estado principal ──────────────────────────────────────
    const [session, setSession]         = useState(null);  // Sessão Supabase
    const [user, setUser]               = useState(null);  // Usuário autenticado
    const [lojas, setLojas]             = useState([]);    // Lojas permitidas
    const [userRoles, setUserRoles]     = useState([]);    // Cargos do usuário
    const [loadingAuth, setLoadingAuth] = useState(true);  // Aguardando sessão inicial
    const [loadingLojas, setLoadingLojas] = useState(false); // Aguardando query de lojas
    const [isDemo, setIsDemo]           = useState(false); // Usuário em modo demo
    const [permRows, setPermRows]       = useState([]);    // Restrições de acesso (user_permissions)
    // Para qual usuário lojas e restrições já chegaram: até lá nenhuma tela é liberada
    const [lojasLoadedFor, setLojasLoadedFor] = useState(null);
    const [permsLoadedFor, setPermsLoadedFor] = useState(null);

    const lastFetchedUserIdRef = useRef(null);

    /* ── Busca lojas permitidas para o usuário autenticado ─── */
    const fetchLojasPermitidas = useCallback(async (userId, showLoading = true) => {
        if (!userId) return;

        if (showLoading) {
            setLoadingLojas(true);
        }

        try {
            // Super Admin tem acesso a todas as lojas
            if (userId === SUPER_ADMIN_ID) {
                setUserRoles(['admin', 'master', 'proprietario', 'tecnico_senior']);
                const { data, error } = await supabase
                    .from('locations')
                    .select('id, name, is_demo, store_images_url')
                    .eq('is_demo', false) // loja demo é só para quem ainda não tem loja
                    .order('name');

                if (!error && data && data.length > 0) {
                    const todasLojas = data.map(loc => ({ id: loc.id, nome: loc.name, is_demo: loc.is_demo, imagem: loc.store_images_url || null }));
                    setLojas(todasLojas);
                    setIsDemo(false);
                    lastFetchedUserIdRef.current = userId;
                    return;
                }
            }

            const { data, error } = await supabase
                .from('location_permissions')
                .select(`
                    location_id,
                    role,
                    locations (
                        id,
                        name,
                        is_demo,
                        store_images_url
                    )
                `)
                .eq('user_id', userId)
                .is('removed_at', null);

            if (error) {
                console.error('[AuthContext] Erro ao buscar permissões de lojas:', error.message);
                setLojas(await fetchDemoLojas());
                setIsDemo(true);
                return;
            }

            // Extrai os cargos atribuídos nas permissões
            const roles = (data || []).map((row) => (row.role || '').toLowerCase()).filter(Boolean);
            setUserRoles(roles);

            // Normaliza o resultado: extrai o objeto `locations` de cada linha e converte 'name' para 'nome' para a UI
            // `role` é o cargo do próprio usuário nesta loja (usado nas regras de gestão de cargos)
            const lojasPermitidas = (data || [])
                .filter((row) => row.locations)
                .map((row) => ({ id: row.locations.id, nome: row.locations.name, is_demo: row.locations.is_demo, imagem: row.locations.store_images_url || null, role: row.role }));

            if (lojasPermitidas.length === 0) {
                // Usuário sem vínculos → ativa Modo Demo
                setLojas(await fetchDemoLojas());
                setIsDemo(true);
            } else {
                setLojas(lojasPermitidas);
                setIsDemo(false);
            }
            lastFetchedUserIdRef.current = userId;
        } catch (err) {
            console.error('[AuthContext] Falha inesperada ao buscar lojas:', err);
            setLojas(await fetchDemoLojas());
            setIsDemo(true);
        } finally {
            setLoadingLojas(false);
            setLojasLoadedFor(userId);
        }
    }, []);

    /* ── Sincroniza Perfil na Tabela ────────────────────────── */
    const syncUserProfile = async (currentUser) => {
        if (!currentUser) return;
        const avatarUrl = currentUser.user_metadata?.avatar_url || currentUser.user_metadata?.picture || currentUser.user_metadata?.photo_url || currentUser.identities?.[0]?.identity_data?.avatar_url || currentUser.identities?.[0]?.identity_data?.picture || '';
        const fullName = currentUser.user_metadata?.full_name || currentUser.user_metadata?.name || currentUser.email?.split('@')[0] || '';
        
        try {
            await supabase.from('user_profiles').upsert({
                id: currentUser.id,
                email: currentUser.email,
                full_name: fullName,
                avatar_url: avatarUrl,
                picture: avatarUrl
            }, { onConflict: 'id' });
        } catch (e) {
            console.warn('[AuthContext] Falha ao sincronizar perfil do usuário', e);
        }
    };

    /* ── Registra o login no audit_log (uma vez por sessão do navegador) ── */
    const logLogin = (event, currentUser) => {
        if (event !== 'SIGNED_IN') return;
        try {
            const key = `login_logged_${currentUser.id}`;
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
        } catch { /* sem sessionStorage: registra mesmo assim */ }
        supabase.rpc('log_event', { p_action: 'LOGIN', p_summary: 'Entrou no sistema' })
            .then(() => {}, () => {});
    };

    /* ── Escuta mudanças de sessão do Supabase ─────────────── */
    useEffect(() => {
        // Carrega sessão existente (ex: reload da página)
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            setLoadingAuth(false);

            if (session?.user) {
                syncUserProfile(session.user);
                fetchLojasPermitidas(session.user.id, true);
            }
        });

        // Listener para login / logout / token refresh
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session);
            const currentUser = session?.user ?? null;
            setUser(currentUser);

            if (currentUser) {
                syncUserProfile(currentUser);
                logLogin(event, currentUser);
                // Se for TOKEN_REFRESHED, apenas atualizamos a sessão sem disparar refetch das lojas
                if (event === 'TOKEN_REFRESHED') {
                    return;
                }

                // Se o usuário ainda não teve as lojas carregadas, busca com loading
                if (lastFetchedUserIdRef.current !== currentUser.id) {
                    fetchLojasPermitidas(currentUser.id, true);
                } else {
                    // Se já estiver carregado, busca em background silencioso
                    fetchLojasPermitidas(currentUser.id, false);
                }
            } else {
                // Logout → limpa o estado
                lastFetchedUserIdRef.current = null;
                setLojasLoadedFor(null);
                setLojas([]);
                setUserRoles([]);
                setIsDemo(false);
            }
        });

        return () => subscription.unsubscribe();
    }, [fetchLojasPermitidas]);

    /* ── Tempo logado: sinal de presença a cada minuto com a aba visível ── */
    // O servidor soma o tempo entre sinais (máx. 3 min), então aba escondida ou fechada não conta
    useEffect(() => {
        if (!user?.id) return undefined;
        const ping = () => {
            if (document.visibilityState !== 'visible') return;
            supabase.rpc('track_presence').then(() => {}, () => {});
        };
        ping();
        const timer = setInterval(ping, 60000);
        document.addEventListener('visibilitychange', ping);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', ping); };
    }, [user?.id]);

    /* ── Restrições de acesso do próprio usuário ───────────── */
    useEffect(() => {
        const userId = user?.id;
        if (!userId) { setPermRows([]); setPermsLoadedFor(null); return undefined; }
        let alive = true;
        const load = async () => {
            // Tabela ausente/erro = sem restrições (comportamento padrão)
            const { data, error } = await supabase.from('user_permissions').select('*').eq('user_id', userId);
            if (!alive) return;
            const next = error ? [] : data || [];
            // Mesmo conteúdo mantém a referência: evita re-render de todo o app a cada recarga
            setPermRows((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
            setPermsLoadedFor(userId);
        };
        load();
        // Mudança feita por um gestor vale sem novo login: recarrega ao voltar para a aba e a cada minuto
        window.addEventListener('focus', load);
        const timer = setInterval(load, 60000);
        return () => { alive = false; window.removeEventListener('focus', load); clearInterval(timer); };
    }, [user?.id]);

    const permsReady = !!user && lojasLoadedFor === user.id && permsLoadedFor === user.id;

    // Modo demo: o cliente Supabase recusa gravações antes de chegarem ao banco
    useEffect(() => { setDemoReadOnly(isDemo); }, [isDemo]);

    const can = useMemo(
        () => buildCan(permRows, lojas, isSuperAdmin(user), permsReady, isDemo),
        [permRows, lojas, user, permsReady, isDemo]
    );

    /* ── Função de logout ───────────────────────────────────── */
    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);

    const refetchLojas = useCallback(
        () => user && fetchLojasPermitidas(user.id),
        [user, fetchLojasPermitidas]
    );

    /* ── Valor exposto pelo contexto ────────────────────────── */
    // Memoizado: evita re-render de todos os consumidores de useAuth a cada render do provider
    const value = useMemo(() => ({
        session,
        user,
        lojas,
        userRoles,
        isSeniorOrAbove: isSeniorOrAbove(user, userRoles),
        isDemo,
        can,
        permsReady,
        loadingAuth,
        loadingLojas,
        isAuthenticated: !!session,
        signOut,
        refetchLojas,
    }), [session, user, lojas, userRoles, isDemo, can, permsReady, loadingAuth, loadingLojas, signOut, refetchLojas]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/* ══════════════════════════════════════════════════════════════
   Hook de acesso ao contexto
   ══════════════════════════════════════════════════════════════ */
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth deve ser usado dentro de um <AuthProvider>');
    }
    return ctx;
}
